import { LoaderCircle, Undo2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import {
  formatearFechaHora,
  formatearMedioPago,
  obtenerEstadoPago,
} from '../../utils/gastos'
import {
  compararCambiosVentaPos,
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
  obtenerEstadoEnvasesPos,
  obtenerEstadoVentaPos,
} from '../../utils/pos'
import ModalMensaje from '../ui/ModalMensaje'
import AnularPagoPosModal from './AnularPagoPosModal'

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-medium text-slate-800">
        {valor || '—'}
      </p>
    </div>
  )
}

function MiniDato({ etiqueta, valor, destacado = false }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p
        className={`mt-0.5 truncate ${
          destacado
            ? 'font-semibold text-slate-900'
            : 'text-slate-700'
        }`}
      >
        {valor}
      </p>
    </div>
  )
}

function Seccion({ titulo, children, ultima = false }) {
  return (
    <div
      className={
        ultima ? '' : 'mb-8 border-b border-slate-100 pb-8'
      }
    >
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {titulo}
      </h3>

      {children}
    </div>
  )
}

function DetalleVentaPosModal({
  abierto,
  venta,
  puedeAnular = false,
  onCerrar,
  onCambio,
}) {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal } = useModalMensaje()

  const [detalles, setDetalles] = useState([])
  const [pagos, setPagos] = useState([])
  const [historial, setHistorial] = useState([])
  const [nombreEliminador, setNombreEliminador] = useState('')
  const [nombrePagadorEnvases, setNombrePagadorEnvases] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [pagoAnular, setPagoAnular] = useState(null)
  const [anulandoPago, setAnulandoPago] = useState(false)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape') {
        onCerrar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, onCerrar])

  useEffect(() => {
    if (!abierto || !venta?.id || !empresaActual?.id) return

    cargarDatos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, venta?.id, empresaActual?.id])

  const cargarDatos = async () => {
    if (!venta?.id || !empresaActual?.id) return

    try {
      setCargando(true)
      setError('')

      const consultaEliminador =
        venta.estado === 'anulado' && venta.anulado_por
          ? supabase
              .from('usuarios_empresas')
              .select('perfil:perfiles(nombre_completo, correo)')
              .eq('usuario_id', venta.anulado_por)
              .eq('empresa_id', empresaActual.id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null })

      const consultaPagadorEnvases =
        venta.estado_envases === 'pagado' && venta.envases_pagados_por
          ? supabase
              .from('usuarios_empresas')
              .select('perfil:perfiles(nombre_completo, correo)')
              .eq('usuario_id', venta.envases_pagados_por)
              .eq('empresa_id', empresaActual.id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null })

      const [
        respuestaDetalle,
        respuestaPagos,
        respuestaHistorial,
        respuestaEliminador,
        respuestaPagadorEnvases,
      ] = await Promise.all([
        supabase
          .from('vw_ventas_pos_detalle')
          .select('*')
          .eq('venta_id', venta.id)
          .eq('empresa_id', empresaActual.id)
          .order('creado_en', { ascending: true }),
        supabase
          .from('vw_ventas_pos_pagos')
          .select('*')
          .eq('venta_id', venta.id)
          .eq('empresa_id', empresaActual.id)
          .order('fecha_pago', { ascending: false }),
        supabase
          .from('vw_ventas_pos_historial')
          .select('*')
          .eq('venta_id', venta.id)
          .eq('empresa_id', empresaActual.id)
          .order('creado_en', { ascending: false }),
        consultaEliminador,
        consultaPagadorEnvases,
      ])

      if (respuestaDetalle.error) throw respuestaDetalle.error
      if (respuestaPagos.error) throw respuestaPagos.error
      if (respuestaHistorial.error) throw respuestaHistorial.error

      if (!montadoRef.current) return

      setDetalles(respuestaDetalle.data || [])
      setPagos(respuestaPagos.data || [])
      setHistorial(respuestaHistorial.data || [])
      setNombreEliminador(
        respuestaEliminador?.data?.perfil?.nombre_completo ||
          respuestaEliminador?.data?.perfil?.correo ||
          ''
      )
      setNombrePagadorEnvases(
        respuestaPagadorEnvases?.data?.perfil?.nombre_completo ||
          respuestaPagadorEnvases?.data?.perfil?.correo ||
          ''
      )
    } catch (errorCarga) {
      console.error(
        'Error al cargar el detalle de la venta POS:',
        errorCarga
      )

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar el detalle de la venta.'
      )
      setDetalles([])
      setPagos([])
      setHistorial([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const solicitarAnularPago = (pago) => {
    setPagoAnular(pago)
  }

  const confirmarAnularPago = async (motivo) => {
    if (!pagoAnular) return

    try {
      setAnulandoPago(true)

      const { error: errorRpc } = await supabase.rpc(
        'anular_pago_venta_pos',
        {
          p_empresa_id: empresaActual.id,
          p_pago_id: pagoAnular.pago_id,
          p_motivo: motivo,
        }
      )

      if (errorRpc) throw errorRpc

      setPagoAnular(null)

      await cargarDatos()
      onCambio?.()

      abrirModal({
        tipo: 'success',
        titulo: 'Pago anulado',
        mensaje: 'El pago fue anulado correctamente.',
      })
    } catch (errorAnular) {
      console.error(
        'Error al anular pago de venta POS:',
        errorAnular
      )

      setPagoAnular(null)

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo anular',
        mensaje:
          errorAnular.message ||
          'No fue posible anular el pago.',
      })
    } finally {
      setAnulandoPago(false)
    }
  }

  if (!abierto || !venta) return null

  const estadoPago = obtenerEstadoPago(venta.estado_pago)
  const estadoVenta = obtenerEstadoVentaPos(venta.estado)
  const estadoEnvases = obtenerEstadoEnvasesPos(venta.estado_envases)
  const ventaAnulada = venta.estado === 'anulado'

  const alHacerClicFuera = (event) => {
    if (event.target === event.currentTarget) {
      onCerrar?.()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="detalle-venta-pos-titulo"
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-6">
          <div>
            <h2
              id="detalle-venta-pos-titulo"
              className="text-xl font-bold text-slate-900"
            >
              Detalle de la venta
            </h2>

            <div className="mt-2 flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoVenta.clase}`}
              >
                {estadoVenta.etiqueta}
              </span>

              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoPago.clase}`}
              >
                {estadoPago.etiqueta}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          <Seccion titulo="Información general">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Cliente"
                valor={
                  venta.cliente_razon_social || 'Venta sin cliente'
                }
              />

              <Dato
                etiqueta="Fecha de venta"
                valor={formatearFechaPos(venta.fecha_venta)}
              />

              <div className="sm:col-span-2">
                <Dato
                  etiqueta="Observaciones"
                  valor={venta.observaciones}
                />
              </div>
            </div>

            {ventaAnulada && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-red-700">
                  Eliminada
                </p>

                <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Dato
                    etiqueta="Motivo"
                    valor={venta.motivo_anulacion}
                  />

                  <Dato
                    etiqueta="Fecha eliminación"
                    valor={formatearFechaHora(venta.anulado_en)}
                  />

                  <div className="sm:col-span-2">
                    <Dato
                      etiqueta="Usuario que eliminó"
                      valor={nombreEliminador}
                    />
                  </div>
                </div>
              </div>
            )}
          </Seccion>

          <Seccion titulo="Productos">
            {cargando ? (
              <div className="flex items-center justify-center py-10">
                <LoaderCircle
                  size={28}
                  className="animate-spin text-emerald-600"
                />
              </div>
            ) : error ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            ) : detalles.length === 0 ? (
              <p className="text-sm text-slate-500">
                Esta venta no tiene productos registrados.
              </p>
            ) : (
              <>
                {/* Mobile: tarjetas apiladas en vez de tabla ancha. */}
                <div className="space-y-3 md:hidden">
                  {detalles.map((detalle) => (
                    <div
                      key={detalle.detalle_id}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <p className="font-semibold text-slate-900">
                        {detalle.producto_codigo} —{' '}
                        {detalle.producto_nombre}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500">
                        {detalle.envase_nombre}
                      </p>

                      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                        <MiniDato
                          etiqueta="Cantidad"
                          valor={detalle.cantidad}
                        />

                        <MiniDato
                          etiqueta="Tara unitaria"
                          valor={formatearKilosPos(
                            detalle.tara_unitaria_kg
                          )}
                        />

                        <MiniDato
                          etiqueta="Peso bruto"
                          valor={formatearKilosPos(
                            detalle.peso_bruto_kg
                          )}
                        />

                        <MiniDato
                          etiqueta="Tara total"
                          valor={formatearKilosPos(
                            detalle.tara_total_kg
                          )}
                        />

                        <MiniDato
                          etiqueta="Peso neto"
                          valor={formatearKilosPos(
                            detalle.kilos_netos
                          )}
                          destacado
                        />

                        <MiniDato
                          etiqueta="Valor/kg"
                          valor={formatearMontoPos(
                            detalle.valor_kilo
                          )}
                        />

                        <MiniDato
                          etiqueta="Total línea"
                          valor={formatearMontoPos(
                            detalle.total_linea
                          )}
                          destacado
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tablet / desktop: tabla completa. */}
                <div className="hidden w-full overflow-x-auto rounded-xl border border-slate-200 md:block">
                  <table className="w-full min-w-[880px] text-left text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2.5">Producto</th>
                        <th className="px-4 py-2.5">Envase</th>
                        <th className="px-4 py-2.5 text-right">
                          Cantidad
                        </th>
                        <th className="px-4 py-2.5 text-right">
                          Tara unit.
                        </th>
                        <th className="px-4 py-2.5 text-right">
                          Peso bruto
                        </th>
                        <th className="px-4 py-2.5 text-right">
                          Tara total
                        </th>
                        <th className="px-4 py-2.5 text-right">
                          Peso neto
                        </th>
                        <th className="px-4 py-2.5 text-right">
                          Valor/kg
                        </th>
                        <th className="px-4 py-2.5 text-right">
                          Total línea
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {detalles.map((detalle) => (
                        <tr key={detalle.detalle_id}>
                          <td className="px-4 py-3 text-slate-800">
                            <span className="font-semibold text-slate-900">
                              {detalle.producto_codigo}
                            </span>{' '}
                            — {detalle.producto_nombre}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                            {detalle.envase_nombre}
                          </td>

                          <td className="px-4 py-3 text-right text-slate-600">
                            {detalle.cantidad}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">
                            {formatearKilosPos(
                              detalle.tara_unitaria_kg
                            )}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">
                            {formatearKilosPos(
                              detalle.peso_bruto_kg
                            )}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">
                            {formatearKilosPos(
                              detalle.tara_total_kg
                            )}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">
                            {formatearKilosPos(detalle.kilos_netos)}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">
                            {formatearMontoPos(detalle.valor_kilo)}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">
                            {formatearMontoPos(detalle.total_linea)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Seccion>

          <Seccion titulo="Cajas/Rejillas">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Estado"
                valor={estadoEnvases.etiqueta}
              />

              {venta.estado_envases === 'pagado' &&
                Number(venta.monto_envases_pagado) > 0 && (
                  <>
                    <Dato
                      etiqueta="Monto pagado"
                      valor={formatearMontoPos(
                        venta.monto_envases_pagado
                      )}
                    />

                    <Dato
                      etiqueta="Fecha pago"
                      valor={formatearFechaHora(
                        venta.envases_pagados_en
                      )}
                    />

                    <Dato
                      etiqueta="Usuario"
                      valor={nombrePagadorEnvases}
                    />
                  </>
                )}
            </div>
          </Seccion>

          <Seccion titulo="Pagos">
            {cargando ? null : pagos.length === 0 ? (
              <p className="text-sm text-slate-500">
                Esta venta no tiene pagos registrados.
              </p>
            ) : (
              <>
                {/* Mobile: tarjetas apiladas en vez de tabla ancha. */}
                <div className="space-y-3 md:hidden">
                  {pagos.map((pago) => (
                    <div
                      key={pago.pago_id}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900">
                            {formatearMontoPos(pago.monto)}
                          </p>

                          <p className="mt-0.5 text-xs text-slate-500">
                            {formatearFechaHora(pago.fecha_pago)}
                          </p>
                        </div>

                        {pago.estado === 'anulado' ? (
                          <span className="shrink-0 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                            Anulado
                          </span>
                        ) : (
                          <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Registrado
                          </span>
                        )}
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                        <MiniDato
                          etiqueta="Medio"
                          valor={formatearMedioPago(
                            pago.medio_pago
                          )}
                        />

                        <MiniDato
                          etiqueta="Referencia"
                          valor={pago.referencia || '—'}
                        />
                      </div>

                      {puedeAnular &&
                        !ventaAnulada &&
                        pago.estado !== 'anulado' && (
                          <button
                            type="button"
                            onClick={() =>
                              solicitarAnularPago(pago)
                            }
                            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                          >
                            <Undo2 size={16} />
                            Anular pago
                          </button>
                        )}
                    </div>
                  ))}
                </div>

                {/* Tablet / desktop: tabla completa. */}
                <div className="hidden w-full overflow-x-auto rounded-xl border border-slate-200 md:block">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2.5">Fecha</th>
                        <th className="px-4 py-2.5">Medio</th>
                        <th className="px-4 py-2.5 text-right">
                          Monto
                        </th>
                        <th className="px-4 py-2.5">Referencia</th>
                        <th className="px-4 py-2.5">Estado</th>
                        {puedeAnular && !ventaAnulada && (
                          <th className="px-4 py-2.5 text-right">
                            Acciones
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {pagos.map((pago) => (
                        <tr key={pago.pago_id}>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                            {formatearFechaHora(pago.fecha_pago)}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                            {formatearMedioPago(pago.medio_pago)}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">
                            {formatearMontoPos(pago.monto)}
                          </td>

                          <td className="px-4 py-3 text-slate-600">
                            {pago.referencia || '—'}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3">
                            {pago.estado === 'anulado' ? (
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                                Anulado
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                Registrado
                              </span>
                            )}
                          </td>

                          {puedeAnular && !ventaAnulada && (
                            <td className="whitespace-nowrap px-4 py-3 text-right">
                              {pago.estado !== 'anulado' && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    solicitarAnularPago(pago)
                                  }
                                  title="Anular pago"
                                  aria-label="Anular pago"
                                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                                >
                                  <Undo2 size={14} />
                                  Anular
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Seccion>

          <Seccion titulo="Resumen" ultima={historial.length === 0}>
            {venta.tipo_descuento && venta.tipo_descuento !== 'ninguno' && (
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Subtotal
                  </p>
                  <p className="mt-1 text-lg font-bold text-slate-900">
                    {formatearMontoPos(venta.subtotal_venta)}
                  </p>
                </div>

                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Descuento
                    {venta.tipo_descuento === 'porcentaje'
                      ? ` ${Number(
                          venta.valor_descuento
                        ).toLocaleString('es-CL')}%`
                      : ''}
                  </p>
                  <p className="mt-1 text-lg font-bold text-slate-900">
                    - {formatearMontoPos(venta.monto_descuento)}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Total venta
                </p>
                <p className="mt-1 text-lg font-bold text-slate-900">
                  {formatearMontoPos(venta.total_venta)}
                </p>
              </div>

              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Total pagado
                </p>
                <p className="mt-1 text-lg font-bold text-slate-900">
                  {formatearMontoPos(venta.monto_pagado)}
                </p>
              </div>

              <div className="rounded-xl bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Saldo pendiente
                </p>
                <p className="mt-1 text-lg font-bold text-emerald-900">
                  {formatearMontoPos(venta.saldo_pendiente)}
                </p>
              </div>
            </div>
          </Seccion>

          {historial.length > 0 && (
            <Seccion titulo="Historial" ultima>
              <div className="space-y-3">
                {historial.map((registro) => {
                  const cambios = compararCambiosVentaPos(
                    registro.datos_anteriores,
                    registro.datos_nuevos
                  )

                  return (
                    <div
                      key={registro.id}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatearFechaHora(registro.creado_en)}
                        </p>

                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {registro.accion === 'edicion'
                            ? 'Edición'
                            : registro.accion}
                        </span>
                      </div>

                      <p className="mt-1 text-sm text-slate-600">
                        {registro.usuario_nombre ||
                          registro.usuario_correo ||
                          'Usuario desconocido'}
                      </p>

                      {registro.motivo && (
                        <p className="mt-2 text-sm text-slate-800">
                          {registro.motivo}
                        </p>
                      )}

                      {cambios.length > 0 && (
                        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
                          {cambios.map((cambio) => (
                            <div
                              key={cambio.etiqueta}
                              className="flex flex-wrap items-center gap-1.5 text-xs"
                            >
                              <span className="font-semibold text-slate-500">
                                {cambio.etiqueta}:
                              </span>
                              <span className="text-slate-600">
                                {cambio.antes}
                              </span>
                              <span className="text-slate-400">
                                →
                              </span>
                              <span className="font-semibold text-slate-800">
                                {cambio.despues}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </Seccion>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 p-6">
          <button
            type="button"
            onClick={onCerrar}
            className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 sm:w-auto"
          >
            Cerrar
          </button>
        </div>
      </div>

      <AnularPagoPosModal
        abierto={Boolean(pagoAnular)}
        pago={pagoAnular}
        procesando={anulandoPago}
        onCancelar={() => setPagoAnular(null)}
        onConfirmar={confirmarAnularPago}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default DetalleVentaPosModal
